import {
  ADVICE_PACK_CATALOGUE_KEY,
  DRAFT_NARRATIVE_CATALOGUE_KEY,
  DRAFT_SUITABILITY_CATALOGUE_KEY,
  STARTER_SESSION_CATALOGUE_KEY,
  isAdviceDeckReviewArtefact,
  isAdvisorNotesReviewArtefact,
  isClientNarrativeReviewArtefact,
  isCheatSheetReviewArtefact,
  isStarterSessionDeckReviewArtefact,
  isSuitabilityAdvisorNotesReviewArtefact,
  isSuitabilityLetterReviewArtefact,
} from "./artefactKind";
import type { ArtefactReviewItem } from "./types";

export function artefactsIncludeCheatSheets(
  artefacts: ArtefactReviewItem[],
  catalogueKey?: string | null,
): boolean {
  return artefacts.some((a) => isCheatSheetReviewArtefact(a, catalogueKey));
}

export function artefactsIncludeAdviceDecks(artefacts: ArtefactReviewItem[]): boolean {
  return artefacts.some((a) => isAdviceDeckReviewArtefact(a));
}

export function artefactsIncludeStarterSessionDecks(artefacts: ArtefactReviewItem[]): boolean {
  return artefacts.some((a) => isStarterSessionDeckReviewArtefact(a));
}

export function artefactsIncludeAdvisorNotes(artefacts: ArtefactReviewItem[]): boolean {
  return artefacts.some((a) => isAdvisorNotesReviewArtefact(a));
}

export function artefactsIncludeClientNarratives(artefacts: ArtefactReviewItem[]): boolean {
  return artefacts.some((a) => isClientNarrativeReviewArtefact(a));
}

export function artefactsIncludeSuitabilityLetters(artefacts: ArtefactReviewItem[]): boolean {
  return artefacts.some((a) => isSuitabilityLetterReviewArtefact(a));
}

export function artefactsIncludeSuitabilityAdvisorNotes(artefacts: ArtefactReviewItem[]): boolean {
  return artefacts.some((a) => isSuitabilityAdvisorNotesReviewArtefact(a));
}

export function artefactRowLabel(artefact: ArtefactReviewItem, catalogueKey?: string | null): string {
  if (isClientNarrativeReviewArtefact(artefact)) return "Draft narrative";
  if (isSuitabilityLetterReviewArtefact(artefact)) return "Suitability letter";
  if (isSuitabilityAdvisorNotesReviewArtefact(artefact)) return "Advisor notes";
  if (isAdvisorNotesReviewArtefact(artefact)) return "Advisor notes";
  if (isStarterSessionDeckReviewArtefact(artefact)) return "Starter session deck";
  if (isAdviceDeckReviewArtefact(artefact)) return "Advice pack";
  if (isCheatSheetReviewArtefact(artefact, catalogueKey)) return "Cheat sheet";
  return "LOA";
}

export function artefactsAceSummaryHint(
  artefacts: ArtefactReviewItem[],
  catalogueKey?: string | null,
): string {
  if (artefactsIncludeClientNarratives(artefacts)) {
    return "Ace's ratings are shown for the draft narrative — open Compare to check side by side.";
  }
  if (artefactsIncludeSuitabilityLetters(artefacts) || artefactsIncludeSuitabilityAdvisorNotes(artefacts)) {
    return "Ace's ratings are shown per artefact — read the advisor notes first, then the letter.";
  }
  if (artefactsIncludeAdviceDecks(artefacts) || artefactsIncludeAdvisorNotes(artefacts)) {
    return "Ace's ratings are shown per artefact — open Compare to check side by side.";
  }
  if (artefactsIncludeStarterSessionDecks(artefacts)) {
    return "Ace's ratings are shown for the starter session deck — open Compare to check side by side.";
  }
  if (artefactsIncludeCheatSheets(artefacts, catalogueKey)) {
    return "Ace's ratings are shown for each cheat sheet — open Compare to check side by side.";
  }
  return "Ace's ratings are shown for each LOA — open Compare to check side by side with the letter.";
}

export function artefactsUnsavedHint(
  artefacts: ArtefactReviewItem[],
  canSave: boolean,
  catalogueKey?: string | null,
): string {
  if (artefactsIncludeClientNarratives(artefacts)) {
    return canSave
      ? "Your narrative rating is not saved yet. Click Save changes when you are finished."
      : "Finish rating the draft narrative on the Artefacts tab, then save your changes here.";
  }
  if (artefactsIncludeSuitabilityLetters(artefacts) || artefactsIncludeSuitabilityAdvisorNotes(artefacts)) {
    return canSave
      ? "Your artefact ratings are not saved yet. Click Save changes when you are finished."
      : "Finish rating the suitability letter and advisor notes, then save your changes here.";
  }
  if (artefactsIncludeAdviceDecks(artefacts) || artefactsIncludeAdvisorNotes(artefacts)) {
    return canSave
      ? "Your artefact ratings are not saved yet. Click Save changes when you are finished."
      : "Finish rating each advice-pack artefact on the Artefacts tab, then save your changes here.";
  }
  if (artefactsIncludeStarterSessionDecks(artefacts)) {
    return canSave
      ? "Your deck rating is not saved yet. Click Save changes when you are finished."
      : "Finish rating the starter session deck on the Artefacts tab, then save your changes here.";
  }
  if (!artefactsIncludeCheatSheets(artefacts, catalogueKey)) {
    return canSave
      ? "Your LOA ratings are not saved yet. Click Save changes when you are finished."
      : "Finish rating each LOA on the Artefacts tab, then save your changes here.";
  }
  return canSave
    ? "Your artefact ratings are not saved yet. Click Save changes when you are finished."
    : "Finish rating each artefact on the Artefacts tab, then save your changes here.";
}

export function artefactReviewInstruction(
  artefacts: ArtefactReviewItem[],
  catalogueKey?: string | null,
): string {
  if (artefactsIncludeClientNarratives(artefacts)) {
    return "Check the draft narrative against cheat sheets, fact-find, and risk data. Rate the TLDR, RAG header, and key fields before continuing — say “Fail” when something looks wrong.";
  }
  const hasSuitabilityLetter = artefactsIncludeSuitabilityLetters(artefacts);
  const hasSuitabilityNotes = artefactsIncludeSuitabilityAdvisorNotes(artefacts);
  if (hasSuitabilityLetter && hasSuitabilityNotes) {
    return "Read the advisor notes first, then check the suitability letter against the paraplanner narrative. Rate both artefacts before continuing.";
  }
  if (hasSuitabilityLetter) {
    return "Check the suitability letter against the paraplanner narrative and cheat sheets. Rate the letter before continuing.";
  }
  if (hasSuitabilityNotes) {
    return "Check the advisor notes assess the drafted letter honestly. Rate the notes before continuing.";
  }
  const hasDeck = artefactsIncludeAdviceDecks(artefacts);
  const hasStarterDeck = artefactsIncludeStarterSessionDecks(artefacts);
  const hasNotes = artefactsIncludeAdvisorNotes(artefacts);
  if (hasStarterDeck) {
    return "Check the starter session deck PDF against the fact-find and goals. Rate the deck before continuing — say “Fail” when something looks wrong.";
  }
  if (hasDeck && hasNotes) {
    return "Check the advice pack PDF and advisor notes against the narrative and render summary. Rate each artefact before continuing — say “Fail” when something looks wrong.";
  }
  if (hasDeck) {
    return "Check the advice pack PDF against the narrative's recommended plans and the render summary. Rate the deck before continuing — say “Fail” when something looks wrong.";
  }
  if (hasNotes) {
    return "Check the advisor notes document against the rendered deck and narrative. Rate the notes before continuing — say “Fail” when something looks wrong.";
  }
  const hasCheatSheet = artefactsIncludeCheatSheets(artefacts, catalogueKey);
  if (hasCheatSheet) {
    return "Check the cheat sheet against the source documents and portfolio output. Rate every artefact before continuing — say “Fail” when something looks wrong.";
  }
  return "Check each letter against the plan it was drafted for. Rate every artefact before continuing — say “Fail” when something looks wrong.";
}

export function artefactReviewEmptyMessage(
  artefacts: ArtefactReviewItem[],
  catalogueKey?: string | null,
): string {
  // Artefacts present but no checklist means criteria are missing, not that discovery failed.
  if (artefacts.length > 0) {
    return "Artefact review criteria are not available for this run. You can continue to submit.";
  }
  if (catalogueKey === DRAFT_NARRATIVE_CATALOGUE_KEY) {
    return "No draft narrative artefacts were found for this run. You can continue to submit.";
  }
  if (catalogueKey === DRAFT_SUITABILITY_CATALOGUE_KEY) {
    return "No suitability letter artefacts were found for this run. You can continue to submit.";
  }
  if (catalogueKey === ADVICE_PACK_CATALOGUE_KEY) {
    return "No advice pack artefacts were found for this run. You can continue to submit.";
  }
  if (catalogueKey === STARTER_SESSION_CATALOGUE_KEY) {
    return "No starter session deck artefacts were found for this run. You can continue to submit.";
  }
  const hasCheatSheet = artefactsIncludeCheatSheets(artefacts, catalogueKey);
  if (hasCheatSheet) {
    return "No cheat sheet artefacts were found for this run. You can continue to submit.";
  }
  return "No LOA artefacts were found for this run. You can continue to submit.";
}

export function artefactWizardNextLabel(
  artefacts: ArtefactReviewItem[] | undefined,
  catalogueKey?: string | null,
): string {
  const items = artefacts ?? [];
  if (artefactsIncludeClientNarratives(items)) {
    return "Review draft narrative";
  }
  if (artefactsIncludeSuitabilityLetters(items) || artefactsIncludeSuitabilityAdvisorNotes(items)) {
    return "Review suitability artefacts";
  }
  if (artefactsIncludeAdviceDecks(items) || artefactsIncludeAdvisorNotes(items)) {
    return "Review advice pack artefacts";
  }
  if (artefactsIncludeStarterSessionDecks(items)) {
    return "Review starter session deck";
  }
  if (artefactsIncludeCheatSheets(items, catalogueKey)) {
    return "Review cheat sheet";
  }
  return "Review LOAs";
}
